/**
 * CSV Parser for Financial Data Import
 *
 * Feature: F007 - CSV/Excel Import (Sprint 10)
 *
 * Parses CSV content with 3 columns: Line Item Type, Line Item Label, Amount
 * Maps line item types to P&L or BS categories.
 * Handles edge cases: BOM, trailing newlines, quoted values, separators.
 */

// Valid P&L line item types — synced with ALL_PL_TYPES in server/routes/financial-templates.ts (EDG-531)
export const CSV_IMPORT_PL_DESTINATIONS = [
  // Revenue
  'income_sales',
  'other_income',
  'gross_revenue_sales',
  'less_returns_allowances',
  'less_discounts',
  // Cost of Sales
  'cost_of_goods_sold',
  'materials_purchases',
  'direct_labour',
  'manufacturing_overhead',
  'freight_delivery',
  'subcontractor_costs',
  'inventory_adjustments',
  // Operating Expenses
  'wages_salaries',
  'rent_premises',
  'utilities',
  'insurance',
  'marketing',
  'professional_fees',
  'motor_vehicle_expenses',
  'office_expenses',
  'other_operating_expenses',
  // Personnel
  'salaries_wages',
  'bonuses_commissions',
  'superannuation',
  'payroll_tax',
  'workers_comp',
  'training_development',
  'recruitment',
  'employee_benefits',
  'contractors',
  // Occupancy
  'property_insurance',
  'repairs_maintenance',
  'cleaning',
  'rates_levies',
  // Admin & Office
  'office_supplies',
  'printing_stationery',
  'phone_internet',
  'it_software',
  'it_hardware',
  'it_support',
  'bank_fees',
  'accounting_fees',
  'legal_fees',
  'audit_fees',
  'consulting_fees',
  // Sales & Marketing
  'advertising',
  'digital_seo',
  'website_hosting',
  'sponsorships',
  'travel_entertainment',
  'client_gifts',
  'crm_tools',
  'referral_fees',
  // Motor Vehicle
  'vehicle_lease_finance',
  'fuel',
  'rego_insurance',
  'vehicle_repairs',
  'tolls_parking',
  // Insurance
  'pi_insurance',
  'pl_insurance',
  'business_pack_insurance',
  'cyber_insurance',
  'key_person_insurance',
  // Compliance
  'asic_regulatory',
  'memberships_subscriptions',
  'licence_renewals',
  'cpd_training',
  // Other
  'bad_debts',
  'donations',
  'sundry_expenses',
  // Non-Operating
  'depreciation',
  'amortisation',
  'interest_expense',
  'lease_expense',
  'tax_expense',
  'interest_income',
  'gain_loss_disposal',
  'fx_gain_loss',
  'other_non_operating_income',
  'other_non_operating_expense',
  // SMSF-Specific
  'rental_income_smsf',
  'investment_income_smsf',
  'member_contributions',
  'fund_expenses',
] as const;

const VALID_PL_TYPES: ReadonlySet<string> = new Set(CSV_IMPORT_PL_DESTINATIONS);

// Valid BS line item types — synced with ALL_BS_TYPES in server/routes/financial-templates.ts (EDG-531)
export const CSV_IMPORT_BS_DESTINATIONS = [
  // Current Assets
  'cash_business_account',
  'accounts_receivable',
  'inventory',
  'prepayments',
  'other_current_assets',
  'trade_receivables',
  'related_party_receivables',
  'gst_receivable',
  'work_in_progress',
  'deposits_paid',
  'loans_to_directors',
  // Non-Current Assets
  'property_plant_equipment',
  'investments',
  'intangibles',
  'goodwill',
  'other_non_current_assets',
  'land_and_buildings',
  'plant_and_machinery',
  'motor_vehicles',
  'furniture_and_fittings',
  'right_of_use_assets',
  'investments_in_subsidiaries',
  'deferred_tax_assets',
  // Current Liabilities
  'accounts_payable',
  'short_term_debt',
  'tax_payable',
  'provisions',
  'other_current_liabilities',
  'gst_payable',
  'payg_withholding',
  'superannuation_payable',
  'related_party_payables',
  'current_portion_long_term_debt',
  'lease_liabilities_current',
  'employee_entitlements_current',
  // Non-Current Liabilities
  'long_term_debt',
  'deferred_tax_liabilities',
  'other_non_current_liabilities',
  'lease_liabilities_non_current',
  'related_party_loans',
  'employee_entitlements_non_current',
  'loans_from_directors',
  // Equity
  'share_capital',
  'retained_earnings',
  'reserves',
  'other_equity',
  'current_year_profit_loss',
  'revaluation_reserve',
  'drawings',
  // SMSF-Specific
  'cash_holdings_smsf',
  'property_values_smsf',
  'other_assets_smsf',
  'loan_balance_smsf',
  // Additional types from API (accepted by bulk import endpoint)
  'aged_debtors_30_plus',
  'aged_creditors_30_plus',
  'current_assets',
  'non_current_assets',
  'total_assets',
  'current_liabilities',
  'non_current_liabilities',
  'total_liabilities',
  'total_equity',
  'cash',
] as const;

const VALID_BS_TYPES: ReadonlySet<string> = new Set(CSV_IMPORT_BS_DESTINATIONS);

export interface ParsedCSVRow {
  lineItemType: string;
  label: string;
  amount: number;
  matched: boolean;
  category: 'profitLoss' | 'balanceSheet' | 'unknown';
}

export interface ParsedCSVResult {
  rows: ParsedCSVRow[];
  summary: {
    totalRows: number;
    profitLossCount: number;
    balanceSheetCount: number;
    unmatchedCount: number;
  };
}

// ============================================================================
// Multi-Year CSV Types (#588 — EDG-834)
// ============================================================================

export type CSVShape =
  | { type: 'single-year' }
  | { type: 'multi-year'; fyColumns: string[] };

export interface ParsedMultiYearCSVRow {
  lineItemType: string;
  label: string;
  amounts: Record<string, number>;
  matched: boolean;
  category: 'profitLoss' | 'balanceSheet' | 'unknown';
}

export interface ParsedMultiYearCSVResult {
  fyColumns: string[];
  rows: ParsedMultiYearCSVRow[];
  summary: {
    totalRows: number;
    fyCount: number;
    profitLossCount: number;
    balanceSheetCount: number;
    unmatchedCount: number;
  };
}

/**
 * Parse a single CSV line, handling quoted fields.
 * Returns an array of field values.
 */
function parseCSVLine(line: string): string[] {
  const fields: string[] = [];
  let current = '';
  let inQuotes = false;

  for (let i = 0; i < line.length; i++) {
    const char = line[i];

    if (char === '"') {
      if (inQuotes && i + 1 < line.length && line[i + 1] === '"') {
        // Escaped quote inside quoted field
        current += '"';
        i++;
      } else {
        // Toggle quote mode
        inQuotes = !inQuotes;
      }
    } else if (char === ',' && !inQuotes) {
      fields.push(current.trim());
      current = '';
    } else {
      current += char;
    }
  }

  fields.push(current.trim());
  return fields;
}

/**
 * Determine the category for a given line item type.
 */
function categorise(lineItemType: string): { matched: boolean; category: 'profitLoss' | 'balanceSheet' | 'unknown' } {
  if (VALID_PL_TYPES.has(lineItemType)) {
    return { matched: true, category: 'profitLoss' };
  }
  if (VALID_BS_TYPES.has(lineItemType)) {
    return { matched: true, category: 'balanceSheet' };
  }
  return { matched: false, category: 'unknown' };
}

/**
 * Parse CSV content into structured financial data rows.
 *
 * @param content - Raw CSV string content
 * @returns Parsed result with rows and summary counts
 */
export function parseCSV(content: string): ParsedCSVResult {
  // Strip BOM if present
  let cleaned = content;
  if (cleaned.charCodeAt(0) === 0xFEFF) {
    cleaned = cleaned.slice(1);
  }

  // Normalise line endings and split
  const lines = cleaned.replace(/\r\n/g, '\n').trim().split('\n');

  // Skip header row
  const dataLines = lines.slice(1);

  const rows: ParsedCSVRow[] = [];

  for (const line of dataLines) {
    const trimmed = line.trim();

    // Skip empty lines
    if (!trimmed) continue;

    // Skip separator rows (---)
    if (trimmed.startsWith('---')) continue;

    const fields = parseCSVLine(trimmed);
    const lineItemType = (fields[0] || '').trim();
    const label = (fields[1] || '').trim();
    const amountStr = (fields[2] || '').trim();

    // Skip rows with no line item type
    if (!lineItemType) continue;

    // Skip rows with empty amounts
    if (!amountStr) continue;

    const amount = parseFloat(amountStr);
    if (isNaN(amount)) continue;

    const { matched, category } = categorise(lineItemType);

    rows.push({
      lineItemType,
      label,
      amount,
      matched,
      category,
    });
  }

  const profitLossCount = rows.filter(r => r.category === 'profitLoss').length;
  const balanceSheetCount = rows.filter(r => r.category === 'balanceSheet').length;
  const unmatchedCount = rows.filter(r => !r.matched).length;

  return {
    rows,
    summary: {
      totalRows: rows.length,
      profitLossCount,
      balanceSheetCount,
      unmatchedCount,
    },
  };
}

/**
 * Convert parsed CSV rows into the bulk import API request body.
 */
export function toBulkImportPayload(rows: ParsedCSVRow[]): {
  profitLoss: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }>;
  balanceSheet: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }>;
} {
  const profitLoss = rows
    .filter(r => r.category === 'profitLoss' && r.matched)
    .map(r => ({
      lineItemType: r.lineItemType,
      amount: r.amount,
      customLabel: null,
      notes: null,
    }));

  const balanceSheet = rows
    .filter(r => r.category === 'balanceSheet' && r.matched)
    .map(r => ({
      lineItemType: r.lineItemType,
      amount: r.amount,
      customLabel: null,
      notes: null,
    }));

  return { profitLoss, balanceSheet };
}

// ============================================================================
// Multi-Year CSV Functions (#588 — EDG-834)
// ============================================================================

/** Regex matching FY column headers like FY2023, fy2024, FY 2025 */
const FY_COLUMN_RE = /^fy\s*(\d{4})$/i;

/**
 * Detect whether a CSV header row represents single-year or multi-year format.
 *
 * Single-year: 3 columns (type, label, amount)
 * Multi-year: 2+ columns where columns after the label match FY20XX pattern
 */
export function detectCSVShape(headerLine: string): CSVShape {
  const fields = parseCSVLine(headerLine);
  if (fields.length < 3) return { type: 'single-year' };

  // Columns after the first two (type + label) are potential FY columns
  const potentialFyCols = fields.slice(2);
  const fyColumns: string[] = [];

  for (const col of potentialFyCols) {
    const trimmed = col.trim();
    const match = FY_COLUMN_RE.exec(trimmed);
    if (match) {
      fyColumns.push(`FY${match[1]}`);
    }
  }

  if (fyColumns.length > 0) {
    return { type: 'multi-year', fyColumns };
  }

  return { type: 'single-year' };
}

/**
 * Parse a multi-year CSV file into structured rows with per-FY amounts.
 *
 * Expected format:
 *   account_type, label, FY2023, FY2024, FY2025
 *   income_sales, Revenue, 1000000, 1200000, 1500000
 */
export function parseMultiYearCSV(content: string): ParsedMultiYearCSVResult {
  // Strip BOM
  let cleaned = content;
  if (cleaned.charCodeAt(0) === 0xFEFF) {
    cleaned = cleaned.slice(1);
  }

  // Normalise line endings and split
  const lines = cleaned.replace(/\r\n/g, '\n').trim().split('\n');

  // Find the header line (skip comment rows starting with #)
  let headerIndex = 0;
  while (headerIndex < lines.length && lines[headerIndex].trim().startsWith('#')) {
    headerIndex++;
  }

  if (headerIndex >= lines.length) {
    return { fyColumns: [], rows: [], summary: { totalRows: 0, fyCount: 0, profitLossCount: 0, balanceSheetCount: 0, unmatchedCount: 0 } };
  }

  const headerLine = lines[headerIndex];
  const shape = detectCSVShape(headerLine);
  const fyColumns = shape.type === 'multi-year' ? shape.fyColumns : [];

  const dataLines = lines.slice(headerIndex + 1);
  const rows: ParsedMultiYearCSVRow[] = [];

  for (const line of dataLines) {
    const trimmed = line.trim();
    if (!trimmed) continue;
    if (trimmed.startsWith('---')) continue;
    if (trimmed.startsWith('#')) continue;

    const fields = parseCSVLine(trimmed);
    const lineItemType = (fields[0] || '').trim();
    const label = (fields[1] || '').trim();

    if (!lineItemType) continue;

    const amounts: Record<string, number> = {};
    for (let i = 0; i < fyColumns.length; i++) {
      const amountStr = (fields[i + 2] || '').trim();
      const parsed = parseFloat(amountStr);
      amounts[fyColumns[i]] = isNaN(parsed) ? 0 : parsed;
    }

    const { matched, category } = categorise(lineItemType);

    rows.push({
      lineItemType,
      label,
      amounts,
      matched,
      category,
    });
  }

  const profitLossCount = rows.filter(r => r.category === 'profitLoss').length;
  const balanceSheetCount = rows.filter(r => r.category === 'balanceSheet').length;
  const unmatchedCount = rows.filter(r => !r.matched).length;

  return {
    fyColumns,
    rows,
    summary: {
      totalRows: rows.length,
      fyCount: fyColumns.length,
      profitLossCount,
      balanceSheetCount,
      unmatchedCount,
    },
  };
}

/**
 * Convert multi-year parsed rows into per-FY bulk import payloads.
 * Each FY key maps to a standard { profitLoss, balanceSheet } payload.
 * Rows with zero amounts for a given FY are excluded from that FY's payload.
 */
export function toMultiYearBulkImportPayload(
  rows: ParsedMultiYearCSVRow[],
  fyColumns: string[],
): Record<string, { profitLoss: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }>; balanceSheet: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }> }> {
  const result: Record<string, { profitLoss: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }>; balanceSheet: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }> }> = {};

  for (const fy of fyColumns) {
    const profitLoss: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }> = [];
    const balanceSheet: Array<{ lineItemType: string; amount: number; customLabel: string | null; notes: string | null }> = [];

    for (const row of rows) {
      if (!row.matched) continue;
      const amount = row.amounts[fy] ?? 0;
      if (amount === 0) continue;

      const item = {
        lineItemType: row.lineItemType,
        amount,
        customLabel: null,
        notes: null,
      };

      if (row.category === 'profitLoss') {
        profitLoss.push(item);
      } else if (row.category === 'balanceSheet') {
        balanceSheet.push(item);
      }
    }

    result[fy] = { profitLoss, balanceSheet };
  }

  return result;
}
